'use client';

import { c as _c } from "react/compiler-runtime";
import { Fragment } from 'react';
import { diffJson } from 'diff';
import { Card } from './Card';
import { Body } from '../typography';
export type DiffMode = 'unified' | 'split';
export interface DiffViewProps {
  oldValue: unknown;
  newValue: unknown;
  mode: DiffMode;
}
type LineKind = 'added' | 'removed' | 'unchanged';
interface DiffLine {
  kind: LineKind;
  text: string;
}
interface SplitRow {
  left: DiffLine | null;
  right: DiffLine | null;
}
const KIND_BG: Record<LineKind, string> = {
  added: 'bg-green-50 dark:bg-green-950/20',
  removed: 'bg-red-50 dark:bg-red-950/20',
  unchanged: ''
};
const KIND_PREFIX: Record<LineKind, string> = {
  added: '+',
  removed: '-',
  unchanged: ' '
};
const KIND_PREFIX_COLOR: Record<LineKind, string> = {
  added: 'text-green-700 dark:text-green-400',
  removed: 'text-red-700 dark:text-red-400',
  unchanged: 'text-content-tertiary'
};
function toLines(parts: {
  added: boolean;
  removed: boolean;
  value: string;
}[]): DiffLine[] {
  const lines: DiffLine[] = [];
  for (const part of parts) {
    const kind: LineKind = part.added ? 'added' : part.removed ? 'removed' : 'unchanged';
    const partLines = part.value.split('\n');
    if (partLines.length > 1 && partLines[partLines.length - 1] === '') {
      partLines.pop();
    }
    for (const text of partLines) {
      lines.push({
        kind,
        text
      });
    }
  }
  return lines;
}
function toSplitRows(lines: DiffLine[]): SplitRow[] {
  const rows: SplitRow[] = [];
  let i = 0;
  while (i < lines.length) {
    if (lines[i].kind === 'unchanged') {
      rows.push({
        left: lines[i],
        right: lines[i]
      });
      i += 1;
      continue;
    }
    const removed: DiffLine[] = [];
    const added: DiffLine[] = [];
    while (i < lines.length && lines[i].kind === 'removed') {
      removed.push(lines[i]);
      i += 1;
    }
    while (i < lines.length && lines[i].kind === 'added') {
      added.push(lines[i]);
      i += 1;
    }
    const max = Math.max(removed.length, added.length);
    for (let j = 0; j < max; j += 1) {
      rows.push({
        left: removed[j] ?? null,
        right: added[j] ?? null
      });
    }
  }
  return rows;
}
function UnifiedView(t0) {
  const $ = _c(4);
  const {
    lines
  } = t0;
  let t1;
  if ($[0] !== lines) {
    t1 = lines.map(_temp);
    $[0] = lines;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <Card className="gap-0 overflow-hidden bg-background-secondary py-1 font-mono text-xs">{t1}</Card>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp(line, idx) {
  return <div className={`flex px-3 ${KIND_BG[line.kind]}`} key={idx}><span className={`select-none w-5 shrink-0 ${KIND_PREFIX_COLOR[line.kind]}`}>{KIND_PREFIX[line.kind]}</span><span className="whitespace-pre-wrap break-all flex-1">{line.text || " "}</span></div>;
}
function SplitCell(t0) {
  const $ = _c(3);
  const {
    line
  } = t0;
  const t1 = `px-3 whitespace-pre-wrap break-all ${line ? KIND_BG[line.kind] : ""}`;
  const t2 = line?.text || " ";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
}
function SplitView(t0) {
  const $ = _c(4);
  const {
    rows
  } = t0;
  let t1;
  if ($[0] !== rows) {
    t1 = rows.map(_temp2);
    $[0] = rows;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <Card className="gap-0 overflow-hidden bg-background-secondary py-1 font-mono text-xs grid grid-cols-2 divide-x divide-border-secondary">{t1}</Card>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp2(row, idx) {
  return <Fragment key={idx}><SplitCell line={row.left} /><SplitCell line={row.right} /></Fragment>;
}
function toJsonString(value: unknown): string {
  if (value === undefined) return 'null';
  return JSON.stringify(value, null, 2);
}

/**
 * Generic JSON-value diff. Pretty-prints both sides as JSON and renders a
 * line-level diff in either unified (single column with -/+ prefixes) or
 * split (two columns) mode.
 */
export function DiffView(t0) {
  const $ = _c(10);
  const {
    oldValue,
    newValue,
    mode
  } = t0;
  let T0;
  let t1;
  let t2;
  if ($[0] !== mode || $[1] !== newValue || $[2] !== oldValue) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const parts = diffJson(toJsonString(oldValue), toJsonString(newValue));
      const lines = toLines(parts);
      const hasChanges = lines.some(_temp3);
      if (!hasChanges) {
        let t3;
        if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = <Body className="text-content-tertiary italic" size="xs">No changes</Body>;
          $[6] = t3;
        } else {
          t3 = $[6];
        }
        t2 = t3;
        break bb0;
      }
      if (mode === "unified") {
        t2 = <UnifiedView lines={lines} />;
        break bb0;
      }
      T0 = SplitView;
      t1 = toSplitRows(lines);
    }
    $[0] = mode;
    $[1] = newValue;
    $[2] = oldValue;
    $[3] = T0;
    $[4] = t1;
    $[5] = t2;
  } else {
    T0 = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  let t3;
  if ($[7] !== T0 || $[8] !== t1) {
    t3 = <T0 rows={t1} />;
    $[7] = T0;
    $[8] = t1;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function _temp3(l) {
  return l.kind !== "unchanged";
}